import { HttpError } from "@microbeta/common-frontend";
import { LoadingSpinner } from "@microbeta/ui";
import type React from "react";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthContext } from "../../../../features/auth/AuthContext";
import { useUserProfile } from "../../../../features/auth/hooks/useUserProfile";
import { paths } from "../../../utils/routing";

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { authIsPending, authState } = useAuthContext();
  const {
    data: userProfile,
    isLoading: isLoadingProfile,
    error: profileError,
  } = useUserProfile();

  const { t } = useTranslation();
  const location = useLocation();
  const navigationAttempts = useRef(0);
  const lastNavigationAttempt = useRef(0);
  const hasShownError = useRef(false);

  // Reset navigation attempts when location changes
  useEffect(() => {
    navigationAttempts.current = 0;
    hasShownError.current = false;
  }, []);

  // If auth is still loading, show spinner
  if (authIsPending) {
    return <LoadingSpinner t={t} />;
  }

  // If not authenticated, redirect to signin
  if (!authIsPending && !authState?.idToken) {
    const redirectUrl = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`${paths.signin}?redirectUrl=${encodeURIComponent(redirectUrl)}`}
        replace
      />
    );
  }

  // If profile is still loading, show spinner
  if (isLoadingProfile) {
    return <LoadingSpinner t={t} />;
  }

  // If user doesn't have a profile and not already on onboarding page, redirect to onboarding
  // Handle timeout, database errors, and 404 (profile not found) errors
  // IMPORTANT: Only redirect to onboarding if the user is still authenticated
  if (
    profileError &&
    location.pathname !== "/onboarding" &&
    authState?.idToken
  ) {
    const now = Date.now();
    const timeSinceLastNavigation = now - lastNavigationAttempt.current;

    // Prevent navigation loops: limit to 2 attempts per 10 seconds
    if (navigationAttempts.current >= 2 && timeSinceLastNavigation < 10000) {
      // Log once but don't keep redirecting
      if (!hasShownError.current) {
        console.warn(
          "Profile check failed multiple times, staying on current page:",
          profileError
        );
        hasShownError.current = true;
      }
      // Return children to break the loop
      return <>{children}</>;
    }

    // Check if this is a 404 error (profile not found) or other expected errors
    const is404Error =
      profileError instanceof HttpError && profileError.status === 404;
    const errorMessage =
      profileError instanceof Error
        ? profileError.message
        : String(profileError);
    const isExpectedError =
      is404Error ||
      errorMessage.includes("timeout") ||
      errorMessage.includes("not configured");

    // Redirect to onboarding for expected errors (404, timeout, database issues)
    if (isExpectedError) {
      console.warn(
        "Profile check failed, redirecting to onboarding:",
        is404Error ? "Profile not found (404)" : errorMessage
      );

      navigationAttempts.current++;
      lastNavigationAttempt.current = now;
      return <Navigate to="/onboarding" replace />;
    }
  }

  // If user has a profile but is on onboarding page, redirect to home
  if (userProfile && location.pathname === "/onboarding") {
    return <Navigate to={paths.home} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
